import { ApolloError, MutationHookOptions } from '@apollo/client'
import { useMemo, useState } from 'react'

import { QueriedAutoShip } from '../../components/shop/account/auto_ships/types'
import {
  GetCustomerAutoShipsDocument,
  PutCustomerAutoShipDocument,
  PutCustomerAutoShipMutation,
  PutCustomerAutoShipMutationVariables,
} from '../../graphql/camel'
import { logAndCaptureException } from '../../utils/errorTools'
import { checkQueryResponse } from '../../utils/graphqlTools'
import useCamelMutation from '../useCamelMutation'
import useLogAndCaptureError from '../useLogAndCaptureError'

const updateCacheAfterResume: MutationHookOptions<
  PutCustomerAutoShipMutation,
  PutCustomerAutoShipMutationVariables
>['update'] = (cache, { data }, { variables }) => {
  const query = GetCustomerAutoShipsDocument
  const cachedQuery = cache.readQuery({ query, variables })
  if (cachedQuery?.getCustomerAutoShips && data?.putCustomerAutoShip) {
    // Update the cached AutoShip to 'Resumed' and write it back to the cache
    const resumedAutoShipId = data.putCustomerAutoShip.autoShipId
    const filteredAutoShips = cachedQuery.getCustomerAutoShips.autoShips.filter(
      (autoShip): autoShip is QueriedAutoShip =>
        !!autoShip && autoShip.autoShipId !== resumedAutoShipId,
    )
    const resumedAutoShip = cachedQuery.getCustomerAutoShips.autoShips.find(
      (autoShip): autoShip is QueriedAutoShip =>
        !!autoShip && autoShip.autoShipId === resumedAutoShipId,
    )

    const updatedResumedAutoShip = {
      ...resumedAutoShip,
      status: 'Active',
    } as QueriedAutoShip
    const newAutoShipValues = resumedAutoShip
      ? [...filteredAutoShips, updatedResumedAutoShip]
      : filteredAutoShips

    cache.writeQuery({
      query,
      variables: { customerId: variables?.customerId ?? '' },
      data: {
        getCustomerAutoShips: {
          ...cachedQuery.getCustomerAutoShips,
          autoShips: newAutoShipValues,
          meta: {
            ...cachedQuery.getCustomerAutoShips.meta,
            totalCount: cachedQuery.getCustomerAutoShips.meta.totalCount - 1,
          },
        },
      },
    })
  }
}

export const useAutoShipResume = (autoShip: QueriedAutoShip) => {
  const [resuming, setResuming] = useState(false)

  const [putCustomerAutoShip, { error: resumeError }] = useCamelMutation(
    PutCustomerAutoShipDocument,
    {
      update: updateCacheAfterResume,
    },
  )
  useLogAndCaptureError(resumeError)

  const resumeAutoShip = useMemo(() => {
    return () => {
      setResuming(true)
      putCustomerAutoShip({
        variables: {
          autoShipId: autoShip.autoShipId,
          customerAutoShipUpdateInput: {
            status: 'ACTIVE',
          },
        },
      })
        .then(checkQueryResponse)
        .catch((err: unknown) => {
          if (!(err instanceof ApolloError)) {
            logAndCaptureException(err)
          }
        })
        .finally(() => {
          setResuming(false)
        })
    }
  }, [putCustomerAutoShip, autoShip.autoShipId])

  return { resumeAutoShip, resuming, resumeError }
}
